import { queryOptions } from '@tanstack/react-query';

/**
 * Query factories: the only way UI code reaches accessory data.
 *
 * This file used to carry a long note about `source()` being the seam where a
 * backend would one day land. It landed on 2026-10-26: the catalogue is
 * `public_accessories`, managed by Kabex from Kabex Hub, and the sample list
 * that stood in for it is deleted.
 *
 * There is deliberately NO `env.useMockData` branch, unlike
 * `services/vehicles/queries.ts`. The vehicle mock exists because the stock
 * table can legitimately be empty in a development project; the accessories
 * mock existed because there was no table at all, and keeping invented prices
 * one environment variable away from production is a risk with nothing left to
 * buy. An empty catalogue now renders the page's own empty state, which is the
 * honest answer to an empty catalogue.
 */
import * as source from './accessories.api';
import { type AccessoryListFilters } from './types';

export const accessoryKeys = {
  all: ['accessories'] as const,
  list: (filters: AccessoryListFilters) => [...accessoryKeys.all, 'list', filters] as const,
  detail: (slug: string) => [...accessoryKeys.all, 'detail', slug] as const,
  related: (slug: string) => [...accessoryKeys.all, 'related', slug] as const,
  facets: () => [...accessoryKeys.all, 'facets'] as const,
};

export const accessoryListQuery = (filters: AccessoryListFilters = {}) =>
  queryOptions({
    queryKey: accessoryKeys.list(filters),
    queryFn: () => source.listAccessories(filters),
  });

export const accessoryDetailQuery = (slug: string) =>
  queryOptions({
    queryKey: accessoryKeys.detail(slug),
    queryFn: () => source.getAccessoryBySlug(slug),
  });

export const relatedAccessoriesQuery = (slug: string) =>
  queryOptions({
    queryKey: accessoryKeys.related(slug),
    queryFn: () => source.listRelatedAccessories(slug),
  });

export const accessoryFacetsQuery = () =>
  queryOptions({
    queryKey: accessoryKeys.facets(),
    queryFn: () => source.getAccessoryFacets(),
    staleTime: 5 * 60_000,
  });
